import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  usePatientVisits, useVisit, useInvestigations,
  type VisitDetail, type EmrNote, type PrescriptionRow, type VitalRow,
  type MedicineRow, type LabOrderRow, type InvestigationRequestRow,
} from "@/lib/queries";
import { fmtDate, fmtDateTime } from "@emr/shared/india";
import { Copy } from "lucide-react";

export interface CopySelection {
  subjective: boolean;
  objective: boolean;
  diagnosis: boolean;
  icd10Codes: boolean;
  plan: boolean;
  note: boolean;
  vitals: boolean;
  prescription: boolean;
}

export const DEFAULT_COPY_SELECTION: CopySelection = {
  subjective: true,
  objective: true,
  diagnosis: true,
  icd10Codes: true,
  plan: true,
  note: true,
  vitals: true,
  prescription: true,
};

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  patientId: string;
  currentVisitId: string;
  onCopySelected: (
    src: { note: EmrNote | null; prescription: PrescriptionRow | null; vitals: VitalRow[] },
    sel: CopySelection,
  ) => void;
}

const CHECKBOX_ITEMS: { key: keyof CopySelection; label: string }[] = [
  { key: "subjective", label: "Presenting complaints" },
  { key: "objective", label: "Findings" },
  { key: "diagnosis", label: "Diagnosis" },
  { key: "icd10Codes", label: "ICD-10 codes" },
  { key: "plan", label: "Plan" },
  { key: "note", label: "Additional notes" },
  { key: "vitals", label: "Vitals" },
  { key: "prescription", label: "Prescription" },
];

export function PreviousVisitsDrawer({
  open, onOpenChange, patientId, currentVisitId, onCopySelected,
}: Props) {
  const { data, isLoading } = usePatientVisits(patientId);
  const visits = (data?.rows ?? []).filter((v) => v.id !== currentVisitId);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sel, setSel] = useState<CopySelection>(DEFAULT_COPY_SELECTION);
  const visitDetail = useVisit(selectedId ?? undefined);
  // Investigations catalog — resolves selectedInvestigationIds to names.
  // Same cached query the EMR visit page itself uses, so no extra fetch.
  const { data: invCatalog } = useInvestigations();
  const investigations = invCatalog?.rows ?? [];

  const selectedVisit: VisitDetail | null = visitDetail.data?.visit ?? null;
  const selectedNote: EmrNote | null = visitDetail.data?.note ?? null;
  const selectedPrescriptions: PrescriptionRow[] = visitDetail.data?.prescriptions ?? [];
  const selectedPrescription: PrescriptionRow | null = selectedPrescriptions[0] ?? null;
  const selectedVitals: VitalRow[] = visitDetail.data?.vitals ?? [];
  const selectedLabOrders: LabOrderRow[] = visitDetail.data?.labOrders ?? [];
  const selectedInvReq: InvestigationRequestRow | null = visitDetail.data?.investigationRequest ?? null;

  const allChecked = CHECKBOX_ITEMS.every((i) => sel[i.key]);

  const copySelected = () => {
    const anyChecked = CHECKBOX_ITEMS.some((i) => sel[i.key]);
    if (!anyChecked) return;
    if (!window.confirm("This will overwrite the selected parts of the current visit. Continue?")) return;
    onCopySelected({ note: selectedNote, prescription: selectedPrescription, vitals: selectedVitals }, sel);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col p-0 sm:max-w-4xl">
        <SheetHeader className="shrink-0 border-b border-cream-200 p-4 dark:border-dark-700">
          <SheetTitle className="text-maroon-700 dark:text-amber-500">Previous visits</SheetTitle>
          <SheetDescription>Click a visit to preview. Tick what to copy, then press Copy selected.</SheetDescription>
        </SheetHeader>

        <div className="flex min-h-0 flex-1">
          {/* Visit list — right pane (narrow) */}
          <div className="w-64 shrink-0 overflow-y-auto border-r border-cream-200 p-3 dark:border-dark-700">
            {isLoading ? (
              <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
            ) : visits.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted">No previous visits.</div>
            ) : (
              <div className="space-y-2">
                {visits.map((v) => (
                  <button
                    key={v.id}
                    className={`w-full rounded-lg border p-3 text-left transition-colors ${selectedId === v.id ? "border-maroon-400 bg-cream-50 dark:bg-dark-700" : "border-cream-200 bg-white hover:bg-cream-50/50 dark:border-dark-700 dark:bg-dark-800"}`}
                    onClick={() => setSelectedId(v.id)}
                  >
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-ink dark:text-cream-50">{fmtDate(v.checkInAt)}</span>
                      <span className="text-xs text-muted">{v.doctorName ?? "—"}</span>
                    </div>
                    <div className="mt-1 truncate text-xs text-muted">{v.diagnosis ?? "No diagnosis recorded"}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Preview — left pane (wide) */}
          <div className="min-w-0 flex-1 overflow-y-auto p-4">
            {selectedId && visitDetail.isLoading ? (
              <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-20" />)}</div>
            ) : selectedId && visitDetail.isError ? (
              <div className="flex h-full items-center justify-center text-sm text-muted">
                Failed to load visit details.
              </div>
            ) : !selectedVisit ? (
              <div className="flex h-full items-center justify-center text-sm text-muted">
                Select a visit to preview its details.
              </div>
            ) : (
              <div className="space-y-3">
                {/* Visit summary header — date, doctor, review, checkout */}
                <div className="rounded-lg border border-cream-200 bg-white p-3 dark:border-dark-700 dark:bg-dark-800">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-sm font-semibold text-ink dark:text-cream-50">
                      {fmtDateTime(selectedVisit.checkInAt)}
                    </div>
                    <div className="text-xs text-muted">
                      {selectedVisit.doctorName
                        ? `${selectedVisit.doctorName}${selectedVisit.doctorQualifications ? ` · ${selectedVisit.doctorQualifications}` : ""}${selectedVisit.doctorRegNumber ? ` · Reg: ${selectedVisit.doctorRegNumber}` : ""}`
                        : "No doctor recorded"}
                    </div>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
                    {selectedVisit.clinicUhid && <span>UHID: {selectedVisit.clinicUhid}</span>}
                    {selectedVisit.checkoutAt && <span>Completed: {fmtDateTime(selectedVisit.checkoutAt)}</span>}
                    <span>
                      {selectedVisit.reviewNeeded
                        ? "Review: if needed (PRN)"
                        : selectedVisit.reviewDate
                          ? `Review on: ${fmtDate(selectedVisit.reviewDate)}`
                          : "Review: not scheduled"}
                    </span>
                  </div>
                </div>

                {/* Copy selection */}
                <div className="rounded-lg border border-cream-200 bg-white p-3 dark:border-dark-700 dark:bg-dark-800">
                  <div className="mb-2 flex items-center gap-2">
                    <Checkbox
                      id="copy-all"
                      checked={allChecked}
                      onCheckedChange={(v) => {
                        const checked = v === true;
                        setSel({
                          subjective: checked,
                          objective: checked,
                          diagnosis: checked,
                          icd10Codes: checked,
                          plan: checked,
                          note: checked,
                          vitals: checked,
                          prescription: checked,
                        });
                      }}
                    />
                    <Label htmlFor="copy-all" className="text-sm font-semibold">Select all</Label>
                  </div>
                  <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                    {CHECKBOX_ITEMS.map((item) => (
                      <div key={item.key} className="flex items-center gap-2">
                        <Checkbox
                          id={`copy-${item.key}`}
                          checked={sel[item.key]}
                          onCheckedChange={(v) => setSel({ ...sel, [item.key]: v === true })}
                        />
                        <Label htmlFor={`copy-${item.key}`} className="text-sm">{item.label}</Label>
                      </div>
                    ))}
                  </div>
                  <Button
                    size="sm"
                    className="mt-3 bg-maroon-600 hover:bg-maroon-700 text-cream-50"
                    onClick={copySelected}
                    disabled={!CHECKBOX_ITEMS.some((i) => sel[i.key])}
                  >
                    <Copy size={14} className="mr-1" /> Copy selected
                  </Button>
                </div>

                {/* Clinical notes */}
                <div className="rounded-lg border border-cream-200 bg-white p-3 dark:border-dark-700 dark:bg-dark-800">
                  <div className="text-xs font-semibold text-muted">Clinical notes</div>
                  <div className="mt-1.5 space-y-2 text-sm">
                    <NotePreview label="Presenting Complaints" value={selectedNote?.subjective} />
                    <NotePreview label="Findings" value={selectedNote?.objective} />
                    <NotePreview label="Diagnosis" value={selectedNote?.diagnosis} />
                    {selectedNote?.icd10Codes && selectedNote.icd10Codes.length > 0 && (
                      <div>
                        <div className="text-xs font-semibold text-muted">ICD-10</div>
                        <div className="text-xs">{selectedNote.icd10Codes.map((c) => `${c.code} (${c.description})`).join(", ")}</div>
                      </div>
                    )}
                    <NotePreview label="Plan" value={selectedNote?.plan} />
                    <NotePreview label="Additional Notes" value={selectedNote?.note} />
                    {!selectedNote?.subjective && !selectedNote?.objective && !selectedNote?.diagnosis
                      && !(selectedNote?.icd10Codes && selectedNote.icd10Codes.length > 0)
                      && !selectedNote?.plan && !selectedNote?.note && (
                      <div className="text-xs text-muted">No clinical notes recorded.</div>
                    )}
                  </div>
                </div>

                {/* Prescriptions — every prescription, full medicine details */}
                {selectedPrescriptions.map((rx) =>
                  rx.medicines.length === 0 ? null : (
                    <div key={rx.id} className="rounded-lg border border-cream-200 bg-white p-3 dark:border-dark-700 dark:bg-dark-800">
                      <div className="text-xs font-semibold text-muted">Prescription ({rx.medicines.length} medicine{rx.medicines.length === 1 ? "" : "s"})</div>
                      <ul className="mt-1.5 space-y-2 text-xs">
                        {rx.medicines.map((m) => <MedicinePreview key={m.id} m={m} />)}
                      </ul>
                    </div>
                  ),
                )}

                {/* Investigations */}
                {selectedInvReq && <InvestigationsPreview invReq={selectedInvReq} investigations={investigations} />}

                {/* Lab orders */}
                {selectedLabOrders.length > 0 && (
                  <div className="rounded-lg border border-cream-200 bg-white p-3 dark:border-dark-700 dark:bg-dark-800">
                    <div className="text-xs font-semibold text-muted">Lab orders</div>
                    <div className="mt-1.5 space-y-2 text-xs">
                      {selectedLabOrders.map((o) => (
                        <div key={o.id}>
                          <div className="font-medium text-ink dark:text-cream-50">
                            {fmtDate(o.createdAt)} · <span className="text-muted">{o.status}</span>
                          </div>
                          {o.tests.length === 0 ? (
                            <div className="text-muted">No tests in this order.</div>
                          ) : (
                            <ul className="mt-1 space-y-1">
                              {o.tests.map((t) => (
                                <li key={t.id}>
                                  <span className="font-medium">{t.testName}</span>
                                  <span className="text-muted"> — {t.status}</span>
                                  {t.result && <span className="text-muted"> · Result: {t.result}</span>}
                                  {t.notes && <span className="text-muted"> · {t.notes}</span>}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Vitals — every recorded row with its timestamp */}
                {selectedVitals.length > 0 && <VitalsPreview rows={selectedVitals} />}
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function NotePreview({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-xs font-semibold text-muted">{label}</div>
      <div className="whitespace-pre-wrap text-xs text-ink dark:text-cream-50">{value}</div>
    </div>
  );
}

/** One medicine row — name, strength, composition, frequency/duration
 * (or the full taper protocol), and instructions. Mirrors the columns of
 * PrescriptionTable's read view. */
function MedicinePreview({ m }: { m: MedicineRow }) {
  const hasTaper = !!m.taper && m.taper.length > 0;
  return (
    <li>
      <div className="font-medium text-ink dark:text-cream-50">
        {m.serialNo}. {m.name}{m.strength ? ` ${m.strength}` : ""}
      </div>
      <div className="text-muted">
        {hasTaper ? (
          <span className="text-maroon-600 dark:text-amber-500">
            Escalation / De-escalation protocol — {m.taper!.length} period(s)
          </span>
        ) : (
          <>
            {m.frequency ?? "—"}
            {m.frequency && m.duration ? " · " : ""}
            {m.duration ?? ""}
          </>
        )}
      </div>
      {hasTaper && (
        <ul className="mt-1 ml-3 list-disc space-y-0.5 text-muted">
          {m.taper!.map((s, i) => (
            <li key={i}>
              {fmtDate(s.fromDate)} · {s.frequency} · {s.days} day{s.days === 1 ? "" : "s"}
              {s.instructions ? ` · ${s.instructions}` : ""}
            </li>
          ))}
        </ul>
      )}
      {m.instructions && <div className="text-muted">{m.instructions}</div>}
    </li>
  );
}

/** Investigation request — every selected test (name resolved from the
 * catalog; deleted catalog items fall back to a stub with the id prefix),
 * per-test Region & Side / Clinical Notes for Rad/EP tests, and the three
 * category notes. */
function InvestigationsPreview({
  invReq, investigations,
}: {
  invReq: InvestigationRequestRow;
  investigations: { id: string; name: string; category: string | null }[];
}) {
  const byId = new Map(investigations.map((i) => [i.id, i]));
  const selected = invReq.selectedInvestigationIds.map((id) => ({ id, inv: byId.get(id) }));
  const details = invReq.investigationDetails ?? {};

  return (
    <div className="rounded-lg border border-cream-200 bg-white p-3 dark:border-dark-700 dark:bg-dark-800">
      <div className="text-xs font-semibold text-muted">Investigations ({selected.length} selected)</div>
      {selected.length === 0 ? (
        <div className="mt-1.5 text-xs text-muted">No investigations were selected.</div>
      ) : (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {selected.map(({ id, inv }) => (
            <Badge key={id} variant="secondary" className="max-w-full break-words">
              {inv?.name ?? `Removed test (${id.slice(0, 8)}…)`}
            </Badge>
          ))}
        </div>
      )}
      {/* Per-test Region & Side / Clinical Notes (Rad/EP detail map) */}
      {selected.some(({ id }) => details[id]) && (
        <ul className="mt-2 space-y-1.5 text-xs">
          {selected.map(({ id, inv }) => {
            const d = details[id];
            if (!d || (!d.regionAndSide && !d.clinicalNotes)) return null;
            return (
              <li key={id}>
                <span className="font-medium text-ink dark:text-cream-50">{inv?.name ?? `Removed test (${id.slice(0, 8)}…)`}</span>
                <div className="ml-3 text-muted">
                  {d.regionAndSide && <div>Region & Side: {d.regionAndSide}</div>}
                  {d.clinicalNotes && <div>Clinical notes: {d.clinicalNotes}</div>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <div className="mt-2 space-y-2 text-sm">
        <NotePreview label="Lab notes" value={invReq.notes} />
        <NotePreview label="Radiology notes" value={invReq.radiologyNotes} />
        <NotePreview label="Electrophysiology notes" value={invReq.electrophysiologyNotes} />
      </div>
    </div>
  );
}

/** Every vitals row recorded on the visit, each with its timestamp. */
function VitalsPreview({ rows }: { rows: VitalRow[] }) {
  if (rows.length === 0) return null;
  return (
    <div className="rounded-lg border border-cream-200 bg-white p-3 dark:border-dark-700 dark:bg-dark-800">
      <div className="text-xs font-semibold text-muted">Vitals ({rows.length} recording{rows.length === 1 ? "" : "s"})</div>
      <div className="mt-1.5 space-y-2 text-xs">
        {rows.map((v) => {
          const entries: string[] = [];
          if (v.tempC != null) entries.push(`Temp ${v.tempC}°C`);
          if (v.bpSystolic != null || v.bpDiastolic != null) {
            entries.push(`BP ${v.bpSystolic ?? "—"}/${v.bpDiastolic ?? "—"}`);
          }
          if (v.pulse != null) entries.push(`Pulse ${v.pulse}`);
          if (v.respRate != null) entries.push(`Resp ${v.respRate}`);
          if (v.spO2 != null) entries.push(`SpO₂ ${v.spO2}%`);
          if (v.heightCm != null) entries.push(`Ht ${v.heightCm}cm`);
          if (v.weightKg != null) entries.push(`Wt ${v.weightKg}kg`);
          if (v.bmi != null) entries.push(`BMI ${v.bmi}`);
          return (
            <div key={v.id}>
              <div className="text-[10px] text-muted">{fmtDateTime(v.recordedAt)}</div>
              <div className="text-ink dark:text-cream-50">{entries.length > 0 ? entries.join(" · ") : "No values recorded"}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
